import React, { useState, useEffect, Suspense, ErrorInfo, ReactNode } from 'react';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import About from './components/About';
import Services from './components/Services';
import Process from './components/Process';
import Team from './components/Team';
import Experience from './components/Experience';
import Footer from './components/Footer';
import { ContentProvider, useContent } from './context/ContentContext';
import { Loader2, AlertTriangle } from 'lucide-react';

// Lazy load admin dashboard
const Admin = React.lazy(() => import('./components/Admin'));

const LoadingScreen = () => (
  <div className="min-h-screen flex items-center justify-center bg-slate-50">
    <div className="flex flex-col items-center gap-4">
      <Loader2 className="animate-spin text-[#D4AF37]" size={48} />
      <p className="text-slate-500 font-medium tracking-widest">系统加载中...</p>
    </div>
  </div>
);

interface ErrorBoundaryProps {
  children?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = {
    hasError: false,
    error: null
  };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("UI Rendering Error:", error, errorInfo);
  }

  render() {
    const { hasError, error } = this.state;
    const { children } = this.props;

    if (hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
          <div className="bg-white p-8 rounded-2xl shadow-xl max-w-lg text-center border border-red-100">
            <AlertTriangle className="mx-auto text-red-500 mb-4" size={48} />
            <h2 className="text-2xl font-bold text-slate-800 mb-2">页面加载遇到问题</h2>
            <p className="text-slate-500 mb-6">抱歉，系统渲染时发生了错误。请尝试刷新页面。</p>
            <div className="bg-slate-100 p-4 rounded-lg text-left overflow-auto max-h-40 text-xs text-slate-600 font-mono mb-6">
              {error?.toString()}
            </div>
            <button 
              onClick={() => window.location.reload()}
              className="px-6 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors"
            >
              刷新页面
            </button>
          </div>
        </div>
      );
    }
    return children;
  }
}

function AppContent() {
  const [isAdmin, setIsAdmin] = useState(false);
  const { content } = useContent();

  useEffect(() => {
    const handleHashChange = () => {
      setIsAdmin(window.location.hash === '#admin');
    };

    handleHashChange();
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  if (isAdmin) {
    return (
      <Suspense fallback={<LoadingScreen />}>
        <Admin />
      </Suspense>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main>
        <Hero />
        <About />
        <Services />
        <Process />
        {content.team?.show !== false && <Team />}
        <Experience />
      </main>
      <Footer />
    </div>
  );
}

function App() {
  // Prevent hydration flicker by ensuring we only render client-side state correctly
  const [isMounted, setIsMounted] = useState(false);
  useEffect(() => setIsMounted(true), []);

  if (!isMounted) return <LoadingScreen />;

  return (
    <ErrorBoundary>
      <ContentProvider>
        <AppContent />
      </ContentProvider>
    </ErrorBoundary>
  );
}

export default App;